import React from "react";
import { Info } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import { featBandDb, FEAT_BANDS, FEAT_BAND_HZ } from "utils/wearableExport";

// ── Hover help ──────────────────────────────────────────────────────
// Native `title` is the convention elsewhere in this modal, but it is the wrong
// tool here: it takes a second to appear, truncates, and cannot hold the two or
// three sentences these features actually need. This is a real popover, opened
// on hover AND focus so it is reachable from the keyboard.

export const InfoTip = ({ label, children, align = "left" }) => {
  const { t } = useTranslation();
  return (
    <span className="group relative inline-flex">
      <button
        type="button"
        aria-label={t("analysis.impactFeatures.whatIs", { label })}
        className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full text-[#C4C8CE] transition duration-150 hover:text-[#6B7280] focus:text-[#6B7280] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C7D2FE]"
      >
        <Info size={12} />
      </button>
      <span
        role="tooltip"
        className={`pointer-events-none absolute bottom-full z-20 mb-1.5 w-64 rounded-lg bg-[#111827] px-3 py-2 text-[11px] leading-relaxed text-[#E5E7EB] opacity-0 shadow-lg transition-opacity duration-150 group-focus-within:opacity-100 group-hover:opacity-100 ${
          align === "right" ? "right-0" : "left-0"
        }`}
      >
        {/* No title line: the tip always sits immediately beside the label it
          explains, so repeating it is noise -- and it put the same string in
          the DOM twice. The name lives on the button's aria-label instead. */}
        {children}
      </span>
    </span>
  );
};

// ── Copy ────────────────────────────────────────────────────────────
// Written for someone reading a record at a laptop, not for someone who has
// read the firmware. Every one of these answers "why would I care?".

const HELP_KEYS = {
  freefall: "analysis.impactFeatures.help.freefall",
  gait: "analysis.impactFeatures.help.gait",
  landing: "analysis.impactFeatures.help.landing",
  ringdown: "analysis.impactFeatures.help.ringdown",
  rotation: "analysis.impactFeatures.help.rotation",
  spectrum: "analysis.impactFeatures.help.spectrum",
  window: "analysis.impactFeatures.help.window",
};

// The gate copy carries inline emphasis, so it renders through <Trans>.
const GateHelp = () => (
  <Trans
    i18nKey="analysis.impactFeatures.help.gate"
    components={{ em: <em /> }}
  />
);

// ── Spectrum heatmap ────────────────────────────────────────────────

const ROWS = [
  { group: "lowG", axes: [0, 1, 2] },
  { group: "highG", axes: [3, 4, 5] },
  { group: "gyro", axes: [6, 7, 8] },
];
const AXIS_LABEL = ["X", "Y", "Z"];

// Five steps from paper to indigo. A single hue reads as "more of the same
// thing" — which is what band power is — where a rainbow ramp would invent
// categories that do not exist.
const RAMP = ["#F3F4F6", "#DBE1F5", "#A9B6EC", "#7183DE", "#4338CA"];

const cellColor = (stored, min, max) => {
  if (!stored) return "#FAFAFA";
  const db = featBandDb(stored);
  const span = max - min;
  const t = span > 0.5 ? (db - min) / span : 1;
  return RAMP[
    Math.min(RAMP.length - 1, Math.max(0, Math.round(t * (RAMP.length - 1))))
  ];
};

const Spectrum = ({ features }) => {
  const { t } = useTranslation();
  const flat = features.bands.flat().filter((v) => v > 0);
  if (!flat.length) return null;
  const dbs = flat.map(featBandDb);
  const min = Math.min(...dbs);
  const max = Math.max(...dbs);

  return (
    <div className="mt-2">
      <div className="flex gap-2">
        <div className="w-14 flex-shrink-0" />
        <div className="flex-1">
          {ROWS.map(({ group, axes }) => {
            const muted = group === "gyro" && !features.gyroPresent;
            return (
              <div key={group} className="mb-1.5 last:mb-0">
                <div className="flex items-center gap-2">
                  <span
                    className={`-ml-16 w-14 flex-shrink-0 text-right text-[10px] ${
                      muted ? "text-[#D1D5DB]" : "text-[#6B7280]"
                    }`}
                  >
                    {t(`analysis.impactFeatures.spectrumGroup.${group}`)}
                  </span>
                  <div className="flex-1">
                    {axes.map((axis, i) => (
                      <div key={axis} className="flex items-center gap-1.5">
                        <span
                          className={`w-2 text-[9px] ${
                            muted ? "text-[#E5E7EB]" : "text-[#9CA3AF]"
                          }`}
                        >
                          {AXIS_LABEL[i]}
                        </span>
                        <div className="flex flex-1 gap-px">
                          {features.bands[axis].map((v, b) => (
                            <div
                              key={b}
                              title={
                                muted
                                  ? t("analysis.impactFeatures.gyroNotRunning")
                                  : v
                                  ? `${b * FEAT_BAND_HZ}–${
                                      (b + 1) * FEAT_BAND_HZ
                                    } Hz · ${featBandDb(v).toFixed(1)} dB`
                                  : t("analysis.impactFeatures.bandSilent", {
                                      from: b * FEAT_BAND_HZ,
                                      to: (b + 1) * FEAT_BAND_HZ,
                                    })
                              }
                              className="h-2.5 flex-1 rounded-[1px]"
                              style={{
                                backgroundColor: muted
                                  ? "#FAFAFA"
                                  : cellColor(v, min, max),
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Frequency axis */}
      <div className="mt-1 flex gap-2">
        <div className="w-14 flex-shrink-0" />
        <div className="flex flex-1 items-center gap-1.5">
          <span className="w-2" />
          <div className="flex flex-1 justify-between text-[9px] tabular-nums text-[#C4C8CE]">
            <span>0</span>
            <span>{(FEAT_BANDS * FEAT_BAND_HZ) / 2} Hz</span>
            <span>{FEAT_BANDS * FEAT_BAND_HZ}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Section ─────────────────────────────────────────────────────────

const Metric = ({ label, value, sub, help, muted }) => (
  <div className="py-1.5">
    <dt className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-[#9CA3AF]">
      {label}
      <InfoTip label={label}>{help}</InfoTip>
    </dt>
    <dd
      className={`mt-0.5 text-[14px] font-semibold ${
        muted ? "text-[#C4C8CE]" : "text-[#111827]"
      }`}
    >
      <span>{value}</span>
      {sub && (
        <span className="ml-1 text-[11px] font-normal text-[#9CA3AF]">
          {sub}
        </span>
      )}
    </dd>
  </div>
);

const ImpactFeatures = ({ record }) => {
  const { t } = useTranslation();
  const f = record.features;

  // Pre-v10 firmware never wrote these. Say so plainly rather than rendering a
  // grid of zeros that reads as "measured, and all silent".
  if (!f) {
    return (
      <div className="mt-5 rounded-lg bg-[#F9FAFB] px-3 py-2.5">
        <p className="text-[11px] text-[#6B7280]">
          {t("analysis.impactFeatures.noFeatures", {
            version: record.recVersion,
          })}
        </p>
      </div>
    );
  }

  const g = (mg) => (mg / 1000).toFixed(2);
  const partial = !f.preWindowComplete || !f.postWindowComplete;

  return (
    <div className="mt-5">
      <div className="mb-1 flex items-center justify-between">
        <h4 className="text-[12px] font-semibold text-[#111827]">
          {t("analysis.impactFeatures.title")}
        </h4>
        {partial && (
          <span className="flex items-center gap-1 text-[10px] text-[#B45309]">
            {t("analysis.impactFeatures.partialWindow")}
            <InfoTip
              label={t("analysis.impactFeatures.partialWindow")}
              align="right"
            >
              {t(HELP_KEYS.window)}
            </InfoTip>
          </span>
        )}
      </div>
      <p className="mb-2 text-[11px] leading-relaxed text-[#9CA3AF]">
        {t("analysis.impactFeatures.intro")}
      </p>

      {/* Verdict — what the dry-run gates would have said, plus rotation */}
      <div className="mb-1 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg bg-[#F9FAFB] px-3 py-2">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wide text-[#9CA3AF]">
            {t("analysis.impactFeatures.wouldGateAs")}
          </span>
          <InfoTip label={t("analysis.impactFeatures.dryRunGate")}>
            <GateHelp />
          </InfoTip>
          <span
            className={`text-[12px] font-semibold ${
              record.gateFlagsLabel === "pass"
                ? "text-[#059669]"
                : record.gateFlagsLabel === "unknown"
                ? "text-[#9CA3AF]"
                : "text-[#B45309]"
            }`}
          >
            {record.gateFlagsLabel}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wide text-[#9CA3AF]">
            {t("analysis.impactFeatures.rotation")}
          </span>
          <InfoTip label={t("analysis.impactFeatures.rotation")}>
            {t(HELP_KEYS.rotation)}
          </InfoTip>
          <span
            className={`text-[12px] font-semibold ${
              record.gyroValid ? "text-[#111827]" : "text-[#C4C8CE]"
            }`}
          >
            {record.gyroValid
              ? `${(record.peakGyroMdps / 1000).toFixed(0)} dps`
              : t("analysis.impactFeatures.notMeasured")}
          </span>
        </div>
      </div>

      {/* Context */}
      <dl className="grid grid-cols-2 gap-x-8 sm:grid-cols-4">
        <Metric
          label={t("analysis.impactFeatures.freefall")}
          value={`${g(f.preMinMg)} g`}
          sub={t("analysis.impactFeatures.msBefore", { ms: f.preMinMsBefore })}
          help={t(HELP_KEYS.freefall)}
        />
        <Metric
          label={t("analysis.impactFeatures.gait")}
          value={f.gaitCrossings}
          sub={t("analysis.impactFeatures.footfall", {
            count: f.gaitCrossings,
          })}
          help={t(HELP_KEYS.gait)}
          muted={f.gaitCrossings === 0}
        />
        <Metric
          label={t("analysis.impactFeatures.landing")}
          value={
            f.postPeakMg
              ? `${g(f.postPeakMg)} g`
              : t("analysis.impactFeatures.none")
          }
          sub={
            f.postPeakMg
              ? t("analysis.impactFeatures.msAfter", { ms: f.postPeakMsAfter })
              : null
          }
          help={t(HELP_KEYS.landing)}
          muted={!f.postPeakMg}
        />
        <Metric
          label={t("analysis.impactFeatures.ringdown")}
          value={`${f.decayMs} ms`}
          help={t(HELP_KEYS.ringdown)}
        />
      </dl>

      {/* Spectrum */}
      {f.spectraPresent && (
        <div className="mt-3 border-t border-[#F3F4F6] pt-2.5">
          <div className="flex items-center gap-1">
            <span className="text-[10px] uppercase tracking-wide text-[#9CA3AF]">
              {t("analysis.impactFeatures.frequencySignature")}
            </span>
            <InfoTip label={t("analysis.impactFeatures.frequencySignature")}>
              {t(HELP_KEYS.spectrum)}
            </InfoTip>
          </div>
          <Spectrum features={f} />
        </div>
      )}
    </div>
  );
};

export default ImpactFeatures;
